import { useCallback, useEffect } from 'react';

import { useActorRef } from '@xstate5/react';

import { useAppState } from '@sg/garnish';
import { otaMachine } from '@sg/ota-installer';

import { useFeatureFlag } from '@order/LaunchDarkly';

import { otaLogger } from './helpers';

// ─────────────────────────────────────────────────────────────────────────────

export const useInitOtaActor = () => {
  const appState = useAppState();

  const pollingIntervalInMinutes = useFeatureFlag('CELS-4466-order-in-app-ota-polling-interval');

  // ─── Guards ──────────────────────────────────────────────────────────

  const checkIfAppIsReady = useCallback(() => appState === 'active', [appState]);

  // ─── Refs ────────────────────────────────────────────────────────────

  const actorRef = useActorRef(
    otaMachine.provide({
      delays: { POLLING_INTERVAL: pollingIntervalInMinutes * 60 * 1000 },
      guards: { 'check-if-app-is-ready': checkIfAppIsReady },
    }),
    { input: { logger: otaLogger, shouldUseLaunchTimeGate: true } },
  );

  // ─── Handlers ────────────────────────────────────────────────────────

  const logStateUpdates = useCallback(() => {
    const previousValueRef = { current: actorRef?.getSnapshot?.()?.value };

    const subscription = actorRef?.subscribe?.((snapshot) => {
      const currentValue = snapshot?.value;

      if (currentValue !== previousValueRef.current) {
        otaLogger.debug(`State changed: ${JSON.stringify(currentValue)}`);
        previousValueRef.current = currentValue;
      }
    });

    return () => subscription?.unsubscribe?.();
  }, [actorRef]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(logStateUpdates, [logStateUpdates]);

  // ─────────────────────────────────────────────────────────────────────

  return actorRef;
};
